// components/Pricing/index.tsx
"use client";

import { useContext, useState } from "react";
import { Swords } from "lucide-react";
import { StyledHeroButton } from "@/components/organisms/Landing/Landing.styled";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import SectionTitle from "components/atoms/SectionTitle";
import OfferList from "components/molecules/OfferList";
import PricingBox from "components/molecules/PricingBox";
import { AppContext } from "lib/contexts";
import { AppReducerActionType } from "types";
import { Container, PricingGrid, Section } from "./Pricing.styled";
interface PricingPlanProps {
  name: string;
  price: string;
  yearlyPrice?: string;
  period?: string;
  description: string;
  features: string[];
  featured?: boolean;
}
const Pricing = ({}: {
  isHeading?: boolean;
}) => {
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const handlePricingClick = () => {
    dispatch({
      type: AppReducerActionType.SHOW_PRICING_TABLE,
      payload: {
        ...state,
        showPricingTable: !state.showPricingTable
      }
    });
  };
  const handleSubscribeClick = () => {
    dispatch({
      type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
      payload: {
        ...state,
        showSubscriptionTable: !state.showSubscriptionTable
      }
    });
  };
  const accentText = (text: string) => {
    // Words to emphasize
    const emphasizedWords = ["UNLIMITED", "Watermark-Free", "image editing", "editing", "FLUX.DEV Generations", "Lora", "folders", "Ultimate", "Elite", "power", "free", "2000", "4500"];
    let processedText = text;
    emphasizedWords.forEach(word => {
      const regex = new RegExp(`(${word})`, "gi");
      processedText = processedText.replace(regex,
      // eslint-disable-next-line quotes
      '<span class="bg-gradient-to-r from-sky-400 to-sky-600 text-transparent bg-clip-text font-bold">$1</span>');
    });
    return <span dangerouslySetInnerHTML={{
      __html: processedText
    }} data-sentry-component="accentText" data-sentry-source-file="index.tsx" />;
  };
  const pricingPlans: PricingPlanProps[] = [{
    name: "Free",
    price: "£0",
    yearlyPrice: "£0",
    period: "per month",
    description: "For casual users",
    features: ["10 free gold daily (10 Flux.Dev Generations)", "Limited features", "Image Generation", "Audio Generation", "Video Generation", "Take part in competitions and challenges"]
  }, {
    name: "Plus",
    price: "£9.99",
    yearlyPrice: "£109.99",
    period: "per month",
    description: "For regular users",
    features: ["900 monthly gold (900 Flux.Dev Generations)", "All art styles", "All features", "Editing Functionality", "Organise images with folders", "Save and Load Presets", "Enhanced Image Editing", "1 Free Lora Generation per month", "Watermark-Free Images"]
  }, {
    name: "Elite",
    price: "£19.99",
    yearlyPrice: "£219.99",
    period: "per month",
    description: "For power users!",
    features: ["2000 monthly gold (2000 Regular Model Generations)", "UNLIMITED FLUX.DEV GENERATIONS", "Watermark-Free Images", "All art styles", "All features", "Organise images with folders", "Save and Load Presets", "Enhanced Image Editing", "2 Free Lora Generations per month"],
    featured: true
  }, {
    name: "Ultimate",
    price: "£39.99",
    yearlyPrice: "£439.99",
    period: "per month",
    description: "For the ultimate creator!",
    features: ["4500 monthly gold (4500 Regular Model Generations)", "UNLIMITED FLUX.DEV GENERATIONS", "Watermark-Free Images", "All art styles", "All features", "Organise images with folders", "Save and Load Presets", "Enhanced Image Editing", "3 Free Lora Generations per month"]
  }];
  const [isYearly, setIsYearly] = useState(false);
  return <Section id="pricing" data-sentry-element="Section" data-sentry-component="Pricing" data-sentry-source-file="index.tsx">
            <Container data-sentry-element="Container" data-sentry-source-file="index.tsx">
                {/* Pricing Section */}
                <div className="py-24 relative">
                    <div className="container mx-auto px-4">
                        <div className="max-w-3xl mx-auto text-center mb-16">
                            <h2 className="text-3xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-sky-400 to-sky-600 text-transparent bg-clip-text">
                                Simple, Transparent Pricing
                            </h2>
                            <p className="text-lg text-slate-400">
                                Choose the plan that best fits your needs and
                                start creating today. Whether you&apos;re just
                                getting started or looking to take your fantasy
                                art to the next level, CharGen has a plan for
                                you.
                            </p>
                            <div className="flex items-center justify-center gap-4 mt-8">
                                <span className={cn("text-sm font-medium", !isYearly ? "text-white" : "text-slate-400")}>
                                    Monthly
                                </span>
                                <Switch checked={isYearly} onCheckedChange={setIsYearly} data-sentry-element="Switch" data-sentry-source-file="index.tsx" />
                                <span className={cn("text-sm font-medium", isYearly ? "text-white" : "text-slate-400")}>
                                    Yearly
                                    <span className="ml-2 text-xs text-[#9b87f5]">
                                        (Save up to 20%)
                                    </span>
                                </span>
                            </div>
                        </div>

                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 max-w-8xl mx-auto">
                            {pricingPlans.map((plan, i) => <div key={i} className={cn("relative flex", plan.featured && "md:-mt-8")}>
                                    <Card className={cn("flex flex-col w-full bg-slate-900/50 border-slate-800 backdrop-blur-sm py-4 transition-transform duration-300 hover:scale-105", plan.featured && "border-sky-600 shadow-lg shadow-sky-600/20")}>
                                        <CardContent className="p-6">
                                            <div className="flex flex-col items-center text-center">
                                                <h3 className={cn("text-2xl font-bold mb-2", plan.featured && "bg-gradient-to-r from-sky-400 to-sky-600 text-transparent bg-clip-text")}>
                                                    {plan.name}
                                                </h3>
                                                <p className="text-slate-400 mb-6">
                                                    {plan.description}
                                                </p>
                                                <div className={cn("mb-6", plan.featured && "scale-110")}>
                                                    <span className={cn("text-4xl font-bold", plan.featured && "bg-gradient-to-r from-sky-400 to-sky-600 text-transparent bg-clip-text")}>
                                                        {isYearly ? plan.yearlyPrice : plan.price}
                                                    </span>
                                                    {plan.period && <span className="text-slate-400 ml-2">
                                                            {plan.period}
                                                        </span>}
                                                </div>
                                                <ul className="space-y-4 mb-8 text-left w-full">
                                                    {plan.features.map((feature, j) => <li key={j} className="flex items-center gap-3">
                                                                <Swords className={cn("h-5 w-5 flex-shrink-0", plan.featured ? "text-sky-600" : "text-blue-400")} />
                                                                <span className="text-slate-300">
                                                                    {accentText(feature)}
                                                                </span>
                                                            </li>)}
                                                </ul>
                                                <StyledHeroButton className={cn("w-full", plan.featured && "bg-gradient-to-r from-sky-400 to-sky-600 hover:from-sky-600 hover:to-sky-800")} onClick={handleSubscribeClick}>
                                                    {plan.price === "Free" ? "Get Started" : "Subscribe Now"}
                                                </StyledHeroButton>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>)}
                        </div>
                    </div>
                </div>
            </Container>
            <Container data-sentry-element="Container" data-sentry-source-file="index.tsx">
                <SectionTitle title="Unlock Premium Art Generation with Gold" paragraph={["Perfect for those who prefer flexibility, Gold can be a one-off purchase, giving you the freedom to enhance your art without a subscription.", "For ongoing benefits, consider our Subscription packages, which include varying increments of gold per month, at a cheaper cost than one-off purchases."]} center data-sentry-element="SectionTitle" data-sentry-source-file="index.tsx" />
                <PricingGrid data-sentry-element="PricingGrid" data-sentry-source-file="index.tsx">
                    <PricingBox packageName="300 Gold" price="5.99" subtitle="Kickstart your creativity with the Starter Adventure Pack! Get 300 Gold to explore all models and features. Perfect for newcomers wanting to try premium features. 300 Gold gets you 300 high quality images." buttonText="Buy Now" handleButtonClick={handlePricingClick} data-sentry-element="PricingBox" data-sentry-source-file="index.tsx">
                        <OfferList text="300 Quality Model Images" data-sentry-element="OfferList" data-sentry-source-file="index.tsx" />
                        <OfferList text="30 Premium Generations" data-sentry-element="OfferList" data-sentry-source-file="index.tsx" />
                    </PricingBox>
                    <PricingBox packageName="900 Gold" price="14.99" subtitle="Enhance your artistic journey with the Trove! Gain 900 Gold for creating more advanced images and exploring premium models." buttonText="Buy Now" handleButtonClick={handlePricingClick} data-sentry-element="PricingBox" data-sentry-source-file="index.tsx">
                        <OfferList text="900 Quality Model Images" data-sentry-element="OfferList" data-sentry-source-file="index.tsx" />
                        <OfferList text="90 Premium Generations" data-sentry-element="OfferList" data-sentry-source-file="index.tsx" />
                    </PricingBox>
                    <PricingBox packageName="2000 Gold" price="29.99" subtitle="Elevate your creations with the Treasury! Receive 2000 Gold to unlock premium features, advanced models, and more.." buttonText="Buy Now" handleButtonClick={handlePricingClick} data-sentry-element="PricingBox" data-sentry-source-file="index.tsx">
                        <OfferList text="2000 Quality Model Images" data-sentry-element="OfferList" data-sentry-source-file="index.tsx" />
                        <OfferList text="200 Premium Generations" data-sentry-element="OfferList" data-sentry-source-file="index.tsx" />
                    </PricingBox>
                    <PricingBox packageName="4500 Gold" price="59.99" subtitle="Unleash your full potential with the Dragon Hoard! Get 4500 Gold for unlimited creativity and access to the most exclusive features and models." buttonText="Buy Now" handleButtonClick={handlePricingClick} data-sentry-element="PricingBox" data-sentry-source-file="index.tsx">
                        <OfferList text="4500 Quality Model Images" data-sentry-element="OfferList" data-sentry-source-file="index.tsx" />
                        <OfferList text="450 Premium Generations" data-sentry-element="OfferList" data-sentry-source-file="index.tsx" />
                    </PricingBox>
                </PricingGrid>
            </Container>
        </Section>;
};
export default Pricing;